<template>
  <div class="class-verification-report">
    <!-- Control Bar -->
    <div class="no-print control-bar shadow-lg">
      <div class="d-flex align-items-center">
        <div class="report-icon-container mr-2">
          <feather-icon icon="CheckCircleIcon" size="24" class="text-primary" />
        </div>
        <div>
          <h4 class="mb-0">Class List Verification</h4>
          <b-badge v-show="!loading" variant="light-success" class="mt-25">
            Total Records: {{ reportItems.length }}
          </b-badge>
        </div>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close-btn" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem" />
      <p class="mt-3 font-weight-bold message-text">
        Synchronizing verification records...
      </p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <div class="error-wrapper p-4 animate__animated animate__headShake">
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger mb-3"
        />
        <h4 class="text-danger font-weight-bold">{{ error }}</h4>
        <b-button
          variant="primary"
          @click="loadReportData"
          class="mt-3 px-4 rounded-pill shadow"
          >Retry Sync</b-button
        >
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="reportItems.length === 0"
      class="report-page text-center py-5 shadow-2xl animate__animated animate__fadeIn"
    >
      <feather-icon icon="InboxIcon" size="48" class="text-muted mb-2" />
      <h5 class="text-secondary">No records found for the selected criteria</h5>
      <p class="text-light">Adjust your filters and try again</p>
    </div>

    <!-- Report Content -->
    <div v-else class="report-wrapper animate__animated animate__fadeIn">
      <div
        v-for="(group, groupIndex) in groupedItems"
        :key="groupIndex"
        class="report-page shadow-2xl mb-5"
        :class="{ 'page-break': groupIndex > 0 }"
      >
        <!-- Modern Styled Header -->
        <div class="report-header-modern mb-5">
          <div class="d-flex justify-content-between align-items-start">
            <div class="d-flex align-items-center">
              <div class="logo-wrapper mr-4" v-if="schoolLogo">
                <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
              </div>
              <div class="header-text-block">
                <h1 class="school-name-full mb-1">
                  {{ campusName
                  }}<span v-if="campusBranch" class="text-muted accent-title">
                    — {{ campusBranch }}</span
                  >
                </h1>
                <div class="report-detail-row">
                  <div class="detail-pill mr-2">
                    <feather-icon icon="FileTextIcon" size="12" class="mr-25" />
                    <span class="report-name-text"
                      >Class List Verification</span
                    >
                  </div>
                  <div class="detail-timestamp">
                    <feather-icon icon="CalendarIcon" size="12" class="mr-25" />
                    Generated on: <strong>{{ currentDateTime }}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Prominent Class Headings (Centered over Table) -->
        <div class="prominent-class-heading text-center mb-2 mt-4">
          <h1
            class="mb-1 font-weight-bolder"
            style="
              font-size: 2.2rem;
              letter-spacing: -1px;
              color: #0f172a;
              text-transform: uppercase;
            "
          >
            {{ group.classDetails.class }}
          </h1>
          <h3
            v-if="
              group.classDetails.section &&
              group.classDetails.section !== 'Unassigned Section'
            "
            class="text-secondary font-weight-bold"
            style="letter-spacing: 2px"
          >
            SEC. {{ group.classDetails.section }}
          </h3>
        </div>

        <!-- Data Table with Premium Styling -->
        <div class="table-container shadow-sm mt-4">
          <table class="ledger-style-table">
            <thead>
              <tr class="main-header">
                <th
                  v-if="isColVisible('Sr#')"
                  class="text-center"
                  style="width: 5%"
                >
                  #
                </th>
                <th
                  v-if="isColVisible('GR#')"
                  class="text-center"
                  style="width: 12%"
                >
                  GR NO.
                </th>
                <th
                  v-if="isColVisible('Student Name')"
                  class="text-left"
                  style="width: 28%"
                >
                  STUDENT NAME
                </th>
                <th
                  v-if="isColVisible('Father Name')"
                  class="text-left"
                  style="width: 25%"
                >
                  FATHER NAME
                </th>
                <th
                  v-if="isColVisible('Remarks')"
                  class="text-left"
                  style="width: 30%"
                >
                  REMARKS
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in group.items"
                :key="index"
                :class="index % 2 === 0 ? 'row-even' : 'row-odd'"
              >
                <td
                  v-if="isColVisible('Sr#')"
                  class="text-center serial-cell font-small-3"
                >
                  {{ index + 1 }}
                </td>
                <td
                  v-if="isColVisible('GR#')"
                  class="text-center gr-cell font-small-3"
                >
                  {{ item.grNo || item.gr || "-" }}
                </td>
                <td
                  v-if="isColVisible('Student Name')"
                  class="text-left name-cell"
                >
                  {{ (item.name || item.studentName || "-").toUpperCase() }}
                </td>
                <td
                  v-if="isColVisible('Father Name')"
                  class="text-left father-cell"
                >
                  {{
                    (item.father_name || item.fatherName || "-").toUpperCase()
                  }}
                </td>
                <td
                  v-if="isColVisible('Remarks')"
                  class="text-left remarks-cell"
                >
                  <div class="remarks-box-placeholder"></div>
                </td>
              </tr>
            </tbody>

            <!-- Signature Section (Page Footer in Paper) -->
            <tfoot class="print-footer">
              <tr>
                <td :colspan="visibleColCount">
                  <div class="signature-section-wrapper">
                    <div class="signature-grid">
                      <div
                        v-if="isSignVisible('Admission Officer')"
                        class="signature-block"
                      >
                        <div class="signature-line"></div>
                        <span class="signature-label">Admission Officer</span>
                      </div>
                      <div
                        v-if="isSignVisible('Class Teacher')"
                        class="signature-block"
                      >
                        <div class="signature-line"></div>
                        <span class="signature-label">Class Teacher</span>
                      </div>
                      <div
                        v-if="isSignVisible('Campus Head')"
                        class="signature-block"
                      >
                        <div class="signature-line"></div>
                        <span class="signature-label">Campus Head</span>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "ClassVerificationPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      classID: 0,
      className: "",
      sectionID: 0,
      sectionName: "",
      status: "present",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      currentSessionName: "Current Session",
      visibleCols: [],
      visibleSigns: [],
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    visibleColCount() {
      return this.visibleCols.length;
    },
    groupedItems() {
      if (!this.reportItems || this.reportItems.length === 0) return [];

      const getSequence = (className) => {
        const cls = (className || "").toString().trim().toUpperCase();

        if (cls.includes("PRE-ADMISSION") || cls.includes("PRE ADMISSION"))
          return 0;
        if (cls.includes("PRE NURSERY") || cls.includes("PRE-NURSERY"))
          return 1;
        if (cls.includes("NURSERY")) return 2; // PRE NURSERY will be caught above
        if (cls.includes("KINDER") || cls.includes("KG")) return 3;

        const tokens = cls.replace(/[^A-Z0-9]/g, " ").split(/\s+/);
        if (tokens.includes("XII") || tokens.includes("12")) return 15;
        if (tokens.includes("XI") || tokens.includes("11")) return 14;
        if (tokens.includes("X") || tokens.includes("10")) return 13;
        if (tokens.includes("IX") || tokens.includes("9")) return 12;
        if (tokens.includes("VIII") || tokens.includes("8")) return 11;
        if (tokens.includes("VII") || tokens.includes("7")) return 10;
        if (tokens.includes("VI") || tokens.includes("6")) return 9;
        if (tokens.includes("V") || tokens.includes("5")) return 8;
        if (tokens.includes("IV") || tokens.includes("4")) return 7;
        if (tokens.includes("III") || tokens.includes("3")) return 6;
        if (tokens.includes("II") || tokens.includes("2")) return 5;
        if (tokens.includes("I") || tokens.includes("1")) return 4;

        return 999;
      };

      const groups = {};
      const orderedKeys = [];

      this.reportItems.forEach((item) => {
        const cls = (item.meta_class || "Unassigned Class").toString().trim();
        const sec = (item.meta_section || "Unassigned Section")
          .toString()
          .trim();
        const key = `${cls} - ${sec}`;

        if (!groups[key]) {
          groups[key] = {
            classDetails: { class: cls, section: sec },
            items: [],
          };
          orderedKeys.push(key);
        }
        groups[key].items.push(item);
      });

      return orderedKeys
        .map((key) => groups[key])
        .sort((a, b) => {
          const seqA = getSequence(a.classDetails.class);
          const seqB = getSequence(b.classDetails.class);
          if (seqA !== seqB) return seqA - seqB;

          // Exact alphabetical order for identical sequences with different names (e.g., year bands)
          const classAStr = (a.classDetails.class || "")
            .toString()
            .trim()
            .toUpperCase();
          const classBStr = (b.classDetails.class || "")
            .toString()
            .trim()
            .toUpperCase();
          if (classAStr !== classBStr)
            return classAStr.localeCompare(classBStr);

          // Exact alphabetical order for identical classes
          const secA = (a.classDetails.section || "")
            .toString()
            .trim()
            .toUpperCase();
          const secB = (b.classDetails.section || "")
            .toString()
            .trim()
            .toUpperCase();
          if (secA === "UNASSIGNED SECTION") return 1;
          if (secB === "UNASSIGNED SECTION") return -1;

          return secA.localeCompare(secB);
        });
    },
    reportHash() {
      return Math.random().toString(36).substring(7).toUpperCase();
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || 0;
      this.classID = query.cls || "";
      this.sectionID = query.sec || "";
      this.status = query.status || "present";
      this.visibleCols =
        query.cols !== undefined
          ? query.cols
            ? query.cols.split(",")
            : []
          : ["Sr#", "GR#", "Student Name", "Father Name", "Remarks"];
      this.visibleSigns =
        query.signs !== undefined
          ? query.signs
            ? query.signs.split(",")
            : []
          : ["Admission Officer", "Class Teacher", "Campus Head"];

      if (!this.db) {
        this.error = "System configuration error: Database context missing.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const url = "https://apiv26.myskool.app/api/Students/LoadDataAll";

        const params = {
          db: this.db,
          cID: this.cID,
          clsID: this.classID || 0,
          section: this.sectionID || 0,
          status: this.status,
          sessionID: this.$store.state.sessionID || 0,
        };

        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        let rawData = [];
        if (response.data && response.data.status === "success") {
          const responseData = response.data.data;
          if (
            responseData &&
            !Array.isArray(responseData) &&
            Array.isArray(responseData.data)
          ) {
            rawData = responseData.data;
          } else {
            rawData = responseData || [];
          }
        } else if (Array.isArray(response.data)) {
          rawData = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          rawData = response.data.data;
        }

        this.reportItems = rawData;

        // Fetch session name
        if (this.$store.state.sessionID) {
          const session = (this.$store.state.userData.sessions || []).find(
            (s) => s.id == this.$store.state.sessionID
          );
          if (session) this.currentSessionName = session.session;
        }
      } catch (err) {
        console.error("Error fetching verification report:", err);
        this.error = "Network error or service unavailable.";
      } finally {
        this.loading = false;
      }
    },
    isColVisible(name) {
      return this.visibleCols.includes(name);
    },
    isSignVisible(name) {
      return this.visibleSigns.includes(name);
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Inter:wght@400;600;700&display=swap");

.class-verification-report {
  background: #f4f7f9;
  min-height: 100vh;
  padding: 3rem 2rem;
  font-family: "Outfit", sans-serif;
  color: #2c3e50;
}

.control-bar {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(15px);
  padding: 1.4rem 2.8rem;
  margin-bottom: 2.5rem;
  border-radius: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(226, 232, 240, 0.8);

  h4 {
    color: #1e293b;
    font-weight: 800;
    font-size: 1.3rem;
    letter-spacing: -0.02em;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 11px 26px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  span {
    margin-left: 6px;
  }
  &:hover {
    transform: translateY(-1px);
  }
  &:active {
    transform: scale(0.97);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;
  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
  }
}

.btn-close-btn {
  background: #ffffff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  &:hover {
    color: #ef4444;
    border-color: #fecaca;
  }
}

.report-page {
  background: #ffffff;
  padding: 4.5rem 4rem;
  border-radius: 16px;
  width: 95%;
  max-width: 1250px;
  margin: 0 auto 50px;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2.5px solid #0f172a;
  padding-bottom: 25px;

  .school-logo-img {
    height: 100px;
    width: auto;
  }
  .school-name-full {
    font-size: 1.9rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.03em;
    text-transform: uppercase;
  }

  .report-detail-row {
    display: flex;
    align-items: center;
    margin-top: 10px;
  }
  .detail-pill {
    background: #f1f5f9;
    padding: 4px 14px;
    border-radius: 100px;
    font-weight: 700;
    font-size: 0.85rem;
    color: #334155;
    text-transform: uppercase;
  }
  .detail-timestamp {
    color: #94a3b8;
    font-size: 0.9rem;
    font-weight: 500;
  }
}

.session-badge-container {
  text-align: right;
  border-left: 3px solid #3b82f6;
  padding-left: 20px;
  .session-label {
    font-size: 0.7rem;
    font-weight: 800;
    color: #94a3b8;
    letter-spacing: 0.1em;
  }
  .session-value {
    font-size: 1.4rem;
    font-weight: 900;
    color: #1e293b;
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  border: 1.5px solid #000;
  background: white;

  th,
  td {
    border: 1px solid #0f172a;
    padding: 14px 16px;
    font-size: 0.92rem;
  }

  thead {
    background: #0f172a;
    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      font-size: 0.85rem;
    }
  }

  .row-odd {
    background: #f8fafc;
  }
  .name-cell,
  .gr-cell {
    font-weight: 800;
    color: #1e293b;
  }
  .remarks-box-placeholder {
    height: 28px;
    width: 100%;
  }
}

.signature-section-wrapper {
  margin-top: 70px;
}
.signature-grid {
  display: flex !important;
  justify-content: center !important;
  gap: 100px;
  text-align: center;
}
.signature-block {
  flex: 0 1 260px;
  .signature-line {
    border-bottom: 2px solid #0f172a;
    height: 55px;
    margin-bottom: 12px;
  }
  .signature-label {
    font-weight: 800;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.8rem;
    letter-spacing: 0.05em;
  }
}

.report-final-footer {
  margin-top: 4rem;
  .footer-divider-premium {
    height: 1px;
    background: #e2e8f0;
    width: 100%;
  }
  .footer-tag {
    color: #94a3b8;
    font-size: 0.8rem;
  }
  .footer-meta {
    color: #cbd5e1;
    font-size: 0.75rem;
    font-weight: 600;
  }
}

@page {
  size: A4;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }
  body {
    background: white !important;
    margin: 0 !important;
  }
  .class-verification-report {
    background: white !important;
    padding: 0 !important;
  }

  .page-break {
    page-break-before: always;
  }
  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.8cm 1.2cm !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    box-sizing: border-box !important;
  }

  .ledger-style-table {
    font-size: 9.5pt !important;
    border-width: 2px !important;
    th,
    td {
      padding: 10px 14px !important;
      border-width: 1px !important;
      color: black !important;
      border-color: black !important;
    }
    thead th {
      background: #f0f0f0 !important;
      color: black !important;
      font-weight: 900 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      border-bottom: 2.5px solid black !important;
    }
    .row-odd {
      background: #fafafa !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .name-cell,
    .gr-cell {
      font-weight: 800 !important;
    }
  }

  .report-header-modern {
    margin-bottom: 30px !important;
    padding-bottom: 15px !important;
    border-bottom: 3.5px solid black !important;
    .school-logo-img {
      height: 85px !important;
    }
    .school-name-full {
      font-size: 1.8rem !important;
      color: black !important;
    }
    .detail-pill {
      background: transparent !important;
      border: 1px solid black !important;
    }
  }

  .session-badge-container {
    border-left-color: black !important;
  }
  .signature-section-wrapper {
    margin-top: 100px !important;
  }
  .signature-grid {
    gap: 120px !important;
  }
  .signature-block {
    .signature-line {
      border-bottom: 2.5px solid black !important;
    }
  }
}
</style>
